import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiMenu, FiX, FiSun, FiMoon } from 'react-icons/fi';

const Header = ({ isMenuOpen, setIsMenuOpen }) => {
  const navItems = [
    { name: 'Home', to: '/' },
    { name: 'Experience', to: '/experience' },
    { name: 'Skills', to: '/skills' },
    { name: 'Certifications', to: '/certifications' },
    { name: 'Projects', to: '/projects' },
    { name: 'Contact', to: '/contact' },
  ];

  // Dark mode state
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('theme') === 'dark' || window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  });

  useEffect(() => {
    const html = document.documentElement;
    if (darkMode) {
      html.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      html.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [darkMode]);

  const toggleDarkMode = () => {
    setDarkMode((d) => !d);
  };

  const navLinkClass = ({ isActive }) =>
    `font-medium transition-colors duration-200 text-sm xl:text-base focus:outline-none ${
      isActive
        ? 'text-primary-600 dark:text-primary-400 border-b-2 border-primary-600 dark:border-primary-400 pb-1'
        : 'text-secondary-700 dark:text-white hover:text-primary-600 dark:hover:text-primary-400'
    }`;

  const mobileNavLinkClass = ({ isActive }) =>
    `block w-full text-left px-4 py-3 transition-colors duration-200 touch-target focus:outline-none ${
      isActive
        ? 'text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-dark-200 font-semibold'
        : 'text-secondary-600 dark:text-dark-500 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-dark-200'
    }`;

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 dark:bg-dark-100/80 backdrop-blur-md border-b border-secondary-200 dark:border-dark-200 transition-colors duration-300">
      <div className="container-custom">
        <div className="flex items-center justify-between h-14 sm:h-16">
          {/* Logo */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-center space-x-2"
          >
            <NavLink to="/" className="flex items-center space-x-2 focus:outline-none">
              <div className="w-6 h-6 sm:w-8 sm:h-8 bg-gradient-to-r from-primary-600 to-accent-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-xs sm:text-sm">AT</span>
              </div>
              <span className="text-lg sm:text-xl font-bold gradient-text">Ashar Tariq</span>
            </NavLink>
          </motion.div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-6 xl:space-x-8">
            {navItems.map((item) => (
              <NavLink
                key={item.name}
                to={item.to}
                end={item.to === '/'}
                className={navLinkClass}
              >
                {item.name}
              </NavLink>
            ))}
            {/* Dark/Light Mode Toggle */}
            <button
              onClick={toggleDarkMode}
              className="ml-4 p-2 flex items-center justify-center rounded-full bg-secondary-100 dark:bg-dark-200 text-secondary-700 dark:text-white hover:bg-primary-100 dark:hover:bg-primary-700 transition-colors duration-200 touch-target"
              aria-label="Toggle dark mode"
            >
              {darkMode ? <FiSun className="w-4 h-4 sm:w-5 sm:h-5" /> : <FiMoon className="w-4 h-4 sm:w-5 sm:h-5" />}
            </button>
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="lg:hidden p-2 text-secondary-600 dark:text-dark-500 hover:text-primary-600 dark:hover:text-primary-400 transition-colors duration-200 touch-target"
          >
            {isMenuOpen ? <FiX className="w-5 h-5 sm:w-6 sm:h-6" /> : <FiMenu className="w-5 h-5 sm:w-6 sm:h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <>
            {/* Backdrop for closing menu on outside click */}
            <div
              className="fixed inset-0 z-40 bg-black bg-opacity-30 lg:hidden"
              onClick={() => setIsMenuOpen(false)}
              aria-label="Close menu backdrop"
            />
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden border-t border-secondary-200 dark:border-dark-200 bg-white/95 dark:bg-dark-100/95 backdrop-blur-md z-50"
              style={{ position: 'relative' }}
            >
              <div className="py-4 space-y-2">
                {navItems.map((item) => (
                  <NavLink
                    key={item.name}
                    to={item.to}
                    end={item.to === '/'}
                    className={mobileNavLinkClass}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {item.name}
                  </NavLink>
                ))}
                {/* Dark/Light Mode Toggle (Mobile) */}
                <button
                  onClick={toggleDarkMode}
                  className="mt-2 w-full flex items-center gap-2 px-4 py-3 rounded-full bg-secondary-100 dark:bg-dark-200 text-secondary-600 dark:text-dark-500 hover:bg-primary-100 dark:hover:bg-dark-300 transition-colors duration-200 touch-target"
                  aria-label="Toggle dark mode"
                >
                  {darkMode ? <FiSun className="w-5 h-5" /> : <FiMoon className="w-5 h-5" />}
                  <span className="text-sm sm:text-base">{darkMode ? 'Light Mode' : 'Dark Mode'}</span>
                </button>
              </div>
            </motion.div>
          </>
        )}
      </div>
    </header>
  );
};

export default Header;
